'use client'

import * as React from 'react'
import { cn } from '@klear/ui/lib/utils'

export interface SegmentedControlOption<T extends string = string> {
  value: T
  label: string
  count?: number
  icon?: React.ReactNode
}

export interface SegmentedControlProps<T extends string = string> {
  options: SegmentedControlOption<T>[]
  value: T
  onChange: (value: T) => void
  className?: string
  disabled?: boolean
}

const SegmentedControl = <T extends string = string>({
  options,
  value,
  onChange,
  className,
  disabled = false,
  ...props
}: SegmentedControlProps<T>) => {
  return (
    <div
      className={cn(
        'inline-flex h-9 items-center rounded-2xl bg-muted p-1 text-muted-foreground',
        disabled && 'opacity-50 cursor-not-allowed',
        className
      )}
      {...props}
    >
      {options.map((option) => {
        const isSelected = option.value === value
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => !disabled && onChange(option.value)}
            disabled={disabled}
            className={cn(
              'inline-flex items-center justify-center whitespace-nowrap px-6 py-1 text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 rounded-2xl',
              options.length === 2 && 'w-1/2',
              options.length === 3 && 'w-1/3',
              options.length === 4 && 'w-1/4',
              isSelected ? 'bg-background text-foreground shadow-sm' : 'hover:text-foreground'
            )}
          >
            {option.icon && <span className="mr-2">{option.icon}</span>}
            <span>{option.label}</span>
            {option.count !== undefined && <span className="ml-1 text-xs opacity-70">({option.count})</span>}
          </button>
        )
      })}
    </div>
  )
}

SegmentedControl.displayName = 'SegmentedControl'

export { SegmentedControl }
